import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useSportContent } from '../hooks/useSportContent';
import { getRecoveryUrl } from '../config/recoveryLinks';
import { getSportConfig } from '../config/sportConfigs';
import './RecoveryRehabScreen.css';

const VALID_SPORTS = ['pickleball', 'tennis', 'golf', 'hiking', 'padel', 'badminton', 'racquetball', 'squash', 'running', 'cycling', 'swimming', 'strength'];

const RecoveryRehabScreen: React.FC = () => {
  const { sport: sportParam } = useParams<{ sport?: string }>();
  const navigate = useNavigate();
  const { currentSport, theme } = useTheme();
  const sportId = sportParam && VALID_SPORTS.includes(sportParam.toLowerCase())
    ? sportParam.toLowerCase()
    : currentSport.id;
  const displaySport = sportParam && VALID_SPORTS.includes(sportParam.toLowerCase())
    ? getSportConfig(sportParam.toLowerCase())
    : currentSport;
  const { content, loading, error } = useSportContent(sportId);

  // Only handle invalid sport param; ThemeContext syncs valid sport from URL
  useEffect(() => {
    if (sportParam && !VALID_SPORTS.includes(sportParam.toLowerCase())) {
      navigate('/recovery-rehab', { replace: true });
    }
  }, [sportParam, navigate]);

  const injuryCategory = content?.shotDiagnosis?.find(a => a.id?.includes('injuries'));
  const injuryShots = injuryCategory?.shots ?? [];

  if (loading) {
    return (
      <div className="recovery-rehab-screen">
        <div className="container">
          <h1>Loading Recovery & Rehab...</h1>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recovery-rehab-screen">
        <div className="container">
          <h1>Error Loading Content</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="recovery-rehab-screen">
      <div className="container">
        <h1 style={{ color: theme.colors.primary }}>🏥 Recovery & Rehab</h1>
        <p className="recovery-intro">
          Injury prevention and recovery plans for {displaySport.name}. Select an injury for tips and personalized recovery resources on MendOnBend.
        </p>

        {injuryShots.length > 0 ? (
          <div className="recovery-grid">
            {injuryShots.map((shot) => (
              <div key={shot.name} className="recovery-card">
                <h3>{shot.name}</h3>
                <p className="recovery-description">{shot.description}</p>
                {shot.commonIssues?.length > 0 && (
                  <div className="recovery-issues">
                    <strong>Common causes:</strong>
                    <ul>
                      {shot.commonIssues.map((issue, i) => (
                        <li key={i}>{issue}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {shot.improvements?.length > 0 && (
                  <div className="recovery-tips">
                    <strong>Prevention & recovery:</strong>
                    <ul>
                      {shot.improvements.map((tip, i) => (
                        <li key={i}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
                  <a
                    href={getRecoveryUrl(displaySport.name, shot.name, shot.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="recovery-cta"
                    style={{
                      backgroundColor: theme.colors.primary,
                      color: 'white',
                    }}
                  >
                    Get Recovery Plan on MendOnBend →
                  </a>
                  <Link
                    to="/game-analysis"
                    className="recovery-cta"
                    style={{
                      backgroundColor: 'transparent',
                      color: theme.colors.primary,
                      border: `2px solid ${theme.colors.primary}`,
                    }}
                  >
                    See shot-specific tips →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="recovery-fallback">
            <p>Get personalized recovery plans for common {displaySport.name} injuries.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
              <a
                href={getRecoveryUrl(displaySport.name, 'injury prevention', 'Injuries Overview')}
                target="_blank"
                rel="noopener noreferrer"
                className="recovery-cta"
                style={{
                  backgroundColor: theme.colors.primary,
                  color: 'white',
                }}
              >
                Get Recovery Plan on MendOnBend →
              </a>
              <Link
                to="/game-analysis"
                className="recovery-cta"
                style={{
                  backgroundColor: 'transparent',
                  color: theme.colors.primary,
                  border: `2px solid ${theme.colors.primary}`,
                }}
              >
                See shot-specific tips →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecoveryRehabScreen;
